// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import type {IssueViewerQueryIdentifier} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {useMutation, type UseMutationResult, useQueryClient} from '@github-ui/react-query'
import {transferIssueMutation as transferIssueMutationQueryId} from '@github-ui/server-persisted-queries'
import type {
  TransferIssueInput as ServerTransferIssueInput,
  TransferIssueMutationMutation,
} from '@github-ui/server-persisted-queries/types/transferIssueMutation'

import {issueLifecyclePath, submitIssueLifecycleMutation} from './issue-lifecycle-mutation'
import {removeIssueViewerLifecycleCaches} from './remove-issue-viewer-lifecycle-caches'

export type TransferIssueRouteInput = Omit<ServerTransferIssueInput, 'issueId' | 'clientMutationId'>
export type TransferIssueMutationResponse = TransferIssueMutationMutation

export function useTransferIssueMutation({
  owner,
  repository,
  number,
  primaryIdentifier,
  onCompleted,
  onError,
}: {
  owner: string
  repository: string
  number: number
  primaryIdentifier?: IssueViewerQueryIdentifier
  onCompleted?: (response: TransferIssueMutationMutation) => void | Promise<void>
  onError?: (error: Error) => void
}): UseMutationResult<TransferIssueMutationMutation, Error, {input: TransferIssueRouteInput}> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({input}) => submitTransferIssueMutation({owner, repository, number, input}),
    onSuccess: async response => {
      try {
        await removeIssueViewerLifecycleCaches({owner, repository, number, primaryIdentifier, queryClient})
      } catch (error) {
        reportError(error instanceof Error ? error : new Error('Issue transfer cache cleanup failed', {cause: error}))
      }
      await onCompleted?.(response)
    },
    onError,
  })
}

export function submitTransferIssueMutation({
  owner,
  repository,
  number,
  input,
}: {
  owner: string
  repository: string
  number: number
  input: TransferIssueRouteInput
}) {
  return submitIssueLifecycleMutation<TransferIssueMutationMutation>({
    path: `${issueLifecyclePath(owner, repository, number)}/transfer.json`,
    method: 'POST',
    queryId: transferIssueMutationQueryId,
    input,
    errorMessage: 'Issue transfer mutation failed',
    isSuccess: data => data.transferIssue?.issue != null,
    payloadErrors: data => data.transferIssue?.errors,
  })
}
